import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'
import { useRouter } from 'next/router'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/motion'
import { useLenis } from '@/components/motion/smooth-scroll'

interface PageTransitionProps {
    children: ReactNode
}

type Navigate = (href: string) => void

const TransitionContext = createContext<Navigate | null>(null)

// Internal links call this instead of pushing the route directly, so the
// curtain can close over the old page before the new one renders.
export const usePageTransition = (): Navigate | null => useContext(TransitionContext)

const LABELS: Array<[RegExp, string]> = [
    [/^\/work\/.+/, 'Case study'],
    [/^\/work\/?$/, 'Work'],
    [/^\/services/, 'Services'],
    [/^\/studio/, 'Studio'],
    [/^\/contact/, 'Contact'],
    [/^\/privacy/, 'Privacy'],
    [/^\/$/, 'Home']
]

const labelFor = (href: string): string => {
    const path = href.split(/[?#]/)[0] || '/'
    return LABELS.find(([pattern]) => pattern.test(path))?.[1] ?? 'Fit Design'
}

const PageTransition: FC<PageTransitionProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const lenisRef = useRef(lenis)
    const panelRef = useRef<HTMLDivElement>(null)
    const busyRef = useRef(false)
    const [label, setLabel] = useState('')

    useEffect(() => {
        lenisRef.current = lenis
    }, [lenis])

    const navigate = useCallback<Navigate>((href) => {
        const panel = panelRef.current

        if (busyRef.current) return

        if (!panel || prefersReducedMotion()) {
            void router.push(href)
            return
        }

        busyRef.current = true
        setLabel(labelFor(href))
        lenisRef.current?.stop()

        gsap.timeline()
            .set(panel, { visibility: 'visible', clipPath: 'inset(100% 0% 0% 0%)' })
            .to(panel, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.75, ease: 'expo.inOut' })
            .add(() => {
                void router.push(href)
            })
    }, [router])

    useEffect(() => {
        const panel = panelRef.current

        if (!panel) return

        const reveal = (): void => {
            gsap.to(panel, {
                clipPath: 'inset(0% 0% 100% 0%)',
                duration: 0.9,
                delay: 0.12,
                ease: 'expo.inOut',
                overwrite: true,
                onComplete: () => {
                    gsap.set(panel, { visibility: 'hidden' })
                    busyRef.current = false
                }
            })
        }

        const onStart = (url: string, options: { shallow: boolean }): void => {
            if (options?.shallow || busyRef.current || prefersReducedMotion()) return

            // Browser back and forward: cover instantly, then reveal as usual.
            busyRef.current = true
            setLabel(labelFor(url))
            gsap.killTweensOf(panel)
            gsap.set(panel, { visibility: 'visible', clipPath: 'inset(0% 0% 0% 0%)' })
        }

        const onComplete = (url: string, options: { shallow: boolean }): void => {
            if (options?.shallow) return

            const instance = lenisRef.current
            const hash = url.includes('#') ? url.split('#')[1] : ''

            instance?.start()
            window.scrollTo(0, 0)
            instance?.scrollTo(0, { immediate: true, force: true })

            requestAnimationFrame(() => {
                ScrollTrigger.refresh()

                if (hash) {
                    const target = document.getElementById(decodeURIComponent(hash))

                    if (target && instance) instance.scrollTo(target, { immediate: true, force: true, offset: -16 })
                    else target?.scrollIntoView()
                }

                if (busyRef.current) reveal()
            })
        }

        const onError = (): void => {
            lenisRef.current?.start()
            if (busyRef.current) reveal()
        }

        router.events.on('routeChangeStart', onStart)
        router.events.on('routeChangeComplete', onComplete)
        router.events.on('routeChangeError', onError)

        return () => {
            router.events.off('routeChangeStart', onStart)
            router.events.off('routeChangeComplete', onComplete)
            router.events.off('routeChangeError', onError)
        }
    }, [router.events])

    return (
        <TransitionContext.Provider value={navigate}>
            {children}

            <div
                ref={panelRef}
                aria-hidden='true'
                data-theme='volt'
                className='pointer-events-none invisible fixed inset-0 z-[85] flex flex-col justify-between bg-canvas p-5 text-fore md:p-8'
                style={{ clipPath: 'inset(100% 0% 0% 0%)' }}
            >
                <div className='type-label flex justify-between'>
                    <span>Fit Design</span>
                    <span>Loading</span>
                </div>

                <div className='type-display'>
                    {label}
                </div>
            </div>
        </TransitionContext.Provider>
    )
}

export default PageTransition
